import Snackbar from '@/components/Snackbar';
import { LoadingBox } from '@/components/UI/Box';
import { BackButton } from '@/components/UI/Button';
import { GridMain } from '@/components/UI/Grid';
import {
  ColoredCircularProgress,
  StyledTextField,
  WhiteCircularProgress,
} from '@/components/UI/Typography';
import { Constants, statusLabels } from '@/constants/announcements';
import {
  IconArrowCircleLeftMS,
  IconCloseMS,
  IconDeleteMS,
  IconExpandLessMS,
  IconExpandMoreMS,
  IconPlayCircleMS,
  IconUploadMS,
  IconVisibilityMS,
} from '@/constants/icons';
import { GlobalContext } from '@/context/global';
import { Box, Collapse } from '@mui/material';
import { DragEvent, useContext, useRef, useState } from 'react';

import { ConfirmDialog } from '../components/ConfirmDialog';
import { PreviewModal } from '../components/PreviewModal';
import { isRichTextEmpty, RichTextEditor } from '../components/RichTextEditor';
import { StatusChip } from '../styles';
import { getYoutubeThumbnail } from '../videoUtils';
import {
  AddCardButton,
  CardBody,
  CardBox,
  CardHeader,
  CardHeaderActions,
  CardHeaderTitle,
  CardsColumn,
  CardsHeader,
  CardsTitle,
  CollapseButton,
  DeleteCardButton,
  DraftButton,
  FormContainer,
  ImagePreviewBox,
  ImageUploadButton,
  MediaContainer,
  NameHelper,
  OptionalHelper,
  OptionalLabel,
  PageSubtitle,
  PageTitle,
  PublishButton,
  RemoveImageButton,
  SideButtonsRow,
  SideContainer,
  SidePreviewButton,
  TipsBox,
  TitleContainer,
  VideoPreviewBox,
} from './styles';
import useAnnouncementForm from './useAnnouncementForm';

export function AnnouncementForm(): JSX.Element {
  const {
    isEdit,
    isLoading,
    isSaving,
    status,
    name,
    cards,
    openPublish,
    openConvert,
    openPreview,
    previewCards,
    activeWarning,
    showErrors,
    setName,
    setOpenPublish,
    setOpenConvert,
    setOpenPreview,
    handleAddCard,
    handleRemoveCard,
    handleCardChange,
    handleImageUpload,
    handleRemoveImage,
    handleMoveCard,
    handleToggleCollapse,
    handleSaveDraft,
    handleConfirmConvert,
    handleOpenPublish,
    handleConfirmPublish,
    handleBack,
  } = useAnnouncementForm();

  const { openSnackbar } = useContext(GlobalContext);

  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const handleDragOver = (
    event: DragEvent<HTMLDivElement>,
    index: number
  ): void => {
    event.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (index: number): void => {
    if (dragIndex !== null) handleMoveCard(dragIndex, index);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = (): void => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  if (isLoading) {
    return (
      <GridMain>
        <LoadingBox>
          <ColoredCircularProgress />
        </LoadingBox>
      </GridMain>
    );
  }

  return (
    <GridMain>
      <BackButton onClick={handleBack}>{IconArrowCircleLeftMS}</BackButton>

      <FormContainer>
        <CardsColumn>
          <TitleContainer>
            <PageTitle>
              {isEdit ? Constants.editTitle : Constants.newTitle}
            </PageTitle>
            <StatusChip status={status}>{statusLabels[status]}</StatusChip>
          </TitleContainer>
          {!isEdit && <PageSubtitle>{Constants.formSubtitle}</PageSubtitle>}

          <Box mt={3}>
            <StyledTextField
              label={Constants.nameLabel}
              value={name}
              error={showErrors && !name.trim()}
              helperText={
                showErrors && !name.trim() ? Constants.requiredField : undefined
              }
              onChange={(e) => setName(e.target.value)}
            />
            <NameHelper>{Constants.nameHelper}</NameHelper>
          </Box>

          <CardsHeader>
            <CardsTitle>
              {Constants.cardsSection}
              <span>{Constants.cardCount(cards.length)}</span>
            </CardsTitle>
            <AddCardButton onClick={handleAddCard}>
              {Constants.addCard}
            </AddCardButton>
          </CardsHeader>

          {cards.map((card, index) => (
            <CardBox
              key={card.key}
              draggable
              isDragging={dragIndex === index}
              isDragOver={dragOverIndex === index}
              onDragStart={() => setDragIndex(index)}
              onDragOver={(event) => handleDragOver(event, index)}
              onDrop={() => handleDrop(index)}
              onDragEnd={handleDragEnd}
            >
              <CardHeader onClick={() => handleToggleCollapse(card.key)}>
                <CardHeaderTitle>
                  <h3>{Constants.cardLabel(index + 1)}</h3>
                </CardHeaderTitle>
                <CardHeaderActions>
                  {cards.length > 1 && (
                    <DeleteCardButton
                      onClick={(event) => {
                        event.stopPropagation();
                        handleRemoveCard(card.key);
                      }}
                    >
                      {IconDeleteMS}
                    </DeleteCardButton>
                  )}
                  <CollapseButton>
                    {card.collapsed ? IconExpandMoreMS : IconExpandLessMS}
                  </CollapseButton>
                </CardHeaderActions>
              </CardHeader>

              <Collapse in={!card.collapsed}>
                <CardBody>
                  <Box display="flex" flexDirection="column" gap="16px">
                    <StyledTextField
                      label={Constants.cardTitleLabel}
                      value={card.title}
                      error={showErrors && !card.title.trim()}
                      helperText={
                        showErrors && !card.title.trim()
                          ? Constants.requiredField
                          : undefined
                      }
                      onChange={(e) =>
                        handleCardChange(card.key, 'title', e.target.value)
                      }
                    />
                    <RichTextEditor
                      value={card.description}
                      placeholder={Constants.cardDescriptionLabel}
                      error={showErrors && isRichTextEmpty(card.description)}
                      helperText={Constants.requiredField}
                      onChange={(html) =>
                        handleCardChange(card.key, 'description', html)
                      }
                    />
                  </Box>

                  <OptionalLabel>{Constants.cardOptional}</OptionalLabel>
                  <OptionalHelper>
                    {Constants.cardOptionalHelper}
                  </OptionalHelper>
                  <OptionalHelper>{Constants.imageRatioHelper}</OptionalHelper>

                  {card.imageUrl && (
                    <ImagePreviewBox>
                      <img src={card.imageUrl} alt={card.title} />
                      <RemoveImageButton
                        onClick={() => handleRemoveImage(card.key)}
                      >
                        {IconCloseMS}
                      </RemoveImageButton>
                    </ImagePreviewBox>
                  )}

                  <MediaContainer>
                    <StyledTextField
                      label={Constants.videoLabel}
                      value={card.videoUrl}
                      disabled={Boolean(card.imageUrl)}
                      error={
                        showErrors &&
                        Boolean(card.videoUrl.trim()) &&
                        !getYoutubeThumbnail(card.videoUrl)
                      }
                      helperText={
                        showErrors &&
                        Boolean(card.videoUrl.trim()) &&
                        !getYoutubeThumbnail(card.videoUrl)
                          ? Constants.invalidVideoUrl
                          : undefined
                      }
                      onChange={(e) =>
                        handleCardChange(card.key, 'videoUrl', e.target.value)
                      }
                    />
                    <ImageUploadButton
                      disabled={Boolean(card.videoUrl.trim())}
                      onClick={() => fileInputRefs.current[card.key]?.click()}
                    >
                      {IconUploadMS}
                      {Constants.imageLabel}
                    </ImageUploadButton>
                    <input
                      type="file"
                      accept="image/png, image/jpeg, image/webp"
                      hidden
                      ref={(element) => {
                        fileInputRefs.current[card.key] = element;
                      }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImageUpload(card.key, file);
                        e.target.value = '';
                      }}
                    />
                  </MediaContainer>

                  {getYoutubeThumbnail(card.videoUrl) && (
                    <VideoPreviewBox>
                      <img
                        src={getYoutubeThumbnail(card.videoUrl) ?? ''}
                        alt={card.title}
                      />
                      <span className="play-icon">{IconPlayCircleMS}</span>
                    </VideoPreviewBox>
                  )}
                </CardBody>
              </Collapse>
            </CardBox>
          ))}
        </CardsColumn>

        <SideContainer>
          <SidePreviewButton onClick={() => setOpenPreview(true)}>
            {IconVisibilityMS}
            {Constants.preview}
          </SidePreviewButton>

          <SideButtonsRow>
            <DraftButton onClick={handleSaveDraft} disabled={isSaving}>
              {isSaving ? (
                <ColoredCircularProgress size={20} />
              ) : (
                Constants.saveAsDraft
              )}
            </DraftButton>
            <PublishButton onClick={handleOpenPublish} disabled={isSaving}>
              {isSaving ? (
                <WhiteCircularProgress size={20} />
              ) : (
                Constants.publishAnnouncement
              )}
            </PublishButton>
          </SideButtonsRow>

          <TipsBox>
            <strong>{Constants.tips}</strong>
            <ul>
              <li>{Constants.tipDrag}</li>
              <li>{Constants.tipLimit}</li>
            </ul>
          </TipsBox>
        </SideContainer>
      </FormContainer>

      <ConfirmDialog
        open={openPublish}
        message={Constants.publishMessage(name)}
        warning={activeWarning}
        cancelLabel={Constants.cancel}
        confirmLabel={Constants.publish}
        onCancel={() => setOpenPublish(false)}
        onConfirm={handleConfirmPublish}
      />

      <ConfirmDialog
        open={openConvert}
        message={Constants.convertToDraftMessage(name)}
        cancelLabel={Constants.cancel}
        confirmLabel={Constants.convert}
        onCancel={() => setOpenConvert(false)}
        onConfirm={handleConfirmConvert}
      />

      <PreviewModal
        open={openPreview}
        cards={previewCards}
        onClose={() => setOpenPreview(false)}
      />

      {openSnackbar && <Snackbar />}
    </GridMain>
  );
}
